import { Typography } from '@mui/material';
import useMainFilters from 'components/SearchFilters/hooks/useMainFilters';
import { SlideResultsSummaryData } from 'interfaces/experimentResults';
import { OrchestrationClusterReassignmentOption } from 'interfaces/study';
import { find, first, get, isEmpty, isNumber, map, size, some, toNumber } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useAppSelector } from 'redux/hooks';
import { useClusterTypeOptions } from 'utils/queryHooks/uiConstantsHooks';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import useStudy from 'utils/useStudy';
import useStudyMutations from 'utils/useStudyMutations';
import { ActionStepper } from '../ActionStepper';
import { MapClusterIndexToNeighborhoodName } from './MapClusterIndexToNeighborhoodName';
import SelectSlidesAndOrchestrationsForReassignment from './SelectSlidesAndOrchestrationsForReassignment';

interface AssignNeighborhoodNamesFormProps {
  onClose: (event?: any, force?: boolean) => void;
}

const AssignNeighborhoodNamesForm: FunctionComponent<React.PropsWithChildren<AssignNeighborhoodNamesFormProps>> = ({
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    queryParams: { filters },
  } = useEncodedFilters();
  const { isStudyIdSpecific } = useMainFilters();

  const studyId = get(filters, 'studyId', undefined);
  const studyIdIsSpecific = isStudyIdSpecific(studyId);

  const { study } = useStudy(studyIdIsSpecific ? studyId : undefined);

  const [selectedOrchestrationId, setSelectedOrchestrationId] = useState<string | null>(null);
  const studyOrchestrationReassignments = study?.settings?.clusterIdReassignments?.[selectedOrchestrationId];

  if (size(studyOrchestrationReassignments || []) > 1) {
    console.warn(
      "Study has multiple cluster reassignment mappings for a single orchestration ID. This isn't supported yet.",
      { studyOrchestrationReassignments, selectedOrchestrationId, studyId }
    );
  }

  const currentReassignmentEntry = first(studyOrchestrationReassignments);
  const currentReassignmentsFromStudy = currentReassignmentEntry?.reassignments;
  const currentReassignmentMetadata = currentReassignmentEntry?.metadata;

  const { name: userName } = useAppSelector((state) => state.auth.profile);

  const [selectedSlideResults, setSelectedSlideResults] = useState<SlideResultsSummaryData[]>([]);
  const [clusterAssignmentMapState, setClusterClusterAssignmentMap] = useState<Record<number, string>>(undefined);
  const clusterAssignmentMap: typeof currentReassignmentsFromStudy =
    clusterAssignmentMapState || currentReassignmentsFromStudy || {};

  const [activeStep, setActiveStep] = useState(0);

  const noSelectedSlides = isEmpty(selectedSlideResults);
  const noOrchestrationSelected = !selectedOrchestrationId;
  const noClusterMappings = isEmpty(clusterAssignmentMap);
  const { clusterTypeOptions, isLoadingClusterTypes } = useClusterTypeOptions();

  const hasInvalidClusterMappings = some(
    clusterAssignmentMap,
    (clusterType, genericCluster) =>
      !isNumber(toNumber(genericCluster)) ||
      isNaN(toNumber(genericCluster)) ||
      toNumber(genericCluster) < 0 ||
      !clusterType ||
      !find(clusterTypeOptions, { id: clusterType })
  );

  const isStepFailed = useMemo(
    () => ({
      0: !studyIdIsSpecific || ((noSelectedSlides || noOrchestrationSelected) && activeStep === 1),
      1:
        !studyIdIsSpecific ||
        ((isLoadingClusterTypes || noClusterMappings || hasInvalidClusterMappings) && activeStep === 1),
    }),
    [
      studyIdIsSpecific,
      isLoadingClusterTypes,
      noSelectedSlides,
      noOrchestrationSelected,
      noClusterMappings,
      hasInvalidClusterMappings,
      activeStep,
    ]
  );

  const steps = [
    {
      label: 'Select slides and orchestrations',
      optional: false,
      content: (
        <SelectSlidesAndOrchestrationsForReassignment
          selectedSlideResults={selectedSlideResults}
          selectedOrchestrationId={selectedOrchestrationId}
          setSelectedSlideResults={setSelectedSlideResults}
          setSelectedOrchestrationId={setSelectedOrchestrationId}
        />
      ),
    },
    {
      label: 'Map cluster indices to neighborhood names',
      content: (
        <MapClusterIndexToNeighborhoodName
          clusterAssignmentMap={clusterAssignmentMap}
          metadata={currentReassignmentMetadata}
          setClusterAssignmentMap={setClusterClusterAssignmentMap}
          hasChanges={Boolean(clusterAssignmentMapState)}
        />
      ),
    },
  ];

  const { handleFieldSaveAsync } = useStudyMutations(study);

  const updateStudyClusterIdReassignments = useCallback(async () => {
    const slideIds = map(selectedSlideResults, 'slideId');
    const currentSettings = study?.settings || {};
    const existingReassignments = currentSettings.clusterIdReassignments || {};
    const currentDate = new Date().toISOString();
    const updatedReassignmentsForOrchestration: OrchestrationClusterReassignmentOption[] = [
      {
        reassignments: clusterAssignmentMap,
        metadata: {
          ...currentReassignmentMetadata,
          createdAt: currentReassignmentMetadata?.createdAt || currentDate,
          createdBy: currentReassignmentMetadata?.createdBy || userName,
          updatedAt: currentDate,
          updatedBy: userName,
          slideIds,
        },
      },
    ];
    const newSettings = {
      ...currentSettings,
      clusterIdReassignments: {
        ...existingReassignments,
        [selectedOrchestrationId]: updatedReassignmentsForOrchestration,
      },
    };
    return handleFieldSaveAsync('settings', newSettings);
  }, [
    studyId,
    study?.settings,
    selectedOrchestrationId,
    clusterAssignmentMap,
    selectedSlideResults,
    handleFieldSaveAsync,
  ]);

  return studyIdIsSpecific ? (
    <ActionStepper
      handleSubmit={async () => {
        try {
          await updateStudyClusterIdReassignments();
          enqueueSnackbar('Neighborhood names assigned successfully', { variant: 'success' });
          onClose(undefined, true);
        } catch (error) {
          console.error(error);
        }
      }}
      steps={steps}
      setActiveStepForValidation={setActiveStep}
      isStepFailed={isStepFailed}
      finishText="Save and Close"
    />
  ) : (
    <Typography variant="subtitle1">Please select a specific study to assign neighborhood names.</Typography>
  );
};

export default AssignNeighborhoodNamesForm;
